import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../api/rbacApi";
import {REDIS_LOGS_ENDPOINT,MAX_STREAM_ROWS,DEFAULT_FILTERS,buildApiUrl,extractArray,
  parseStreamPayload,getPayloadMessage,getRunningState,getConnectionState,getBufferSize,
  getMonitorStatusText,normalizeLogEntry,formatDateTime,sanitizeFilters,getErrorMessage,} from "./utils";
import RedisLogFilters from "./RedisLogFilters";
import RedisLogTable from "./RedisLogTable";

const RedisLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULT_FILTERS });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState(null);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [monitorStatus, setMonitorStatus] = useState({
    running: false,
    connected: null,
    bookingBufferSize: null,
    status: "stopped",
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");

  const eventSourceRef = useRef(null);
  const streamIndexRef = useRef(0);

  const closeStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
    setStreamConnected(false);
  }, []);

  const setRowsFromPayload = useCallback((payload, fallbackRunning) => {
    const entries = extractArray(payload);
    const normalized = entries.map((entry, index) => normalizeLogEntry(entry, index));

    setLogs(normalized);
    setMonitorStatus((current) => ({
      running: getRunningState(payload, fallbackRunning ?? current.running),
      connected: getConnectionState(payload, current.connected),
      bookingBufferSize: getBufferSize(payload, current.bookingBufferSize),
      status: getMonitorStatusText(payload, current.status),
    }));
    setStatusMessage((current) => getPayloadMessage(payload) || current);
    setLastUpdatedAt(new Date().toISOString());
  }, []);

  const fetchLogs = useCallback(
    async (requestedFilters = DEFAULT_FILTERS, fallbackRunning) => {
      setLoading(true);
      setError("");

      try {
        const response = await api.get(REDIS_LOGS_ENDPOINT, {
          params: sanitizeFilters(requestedFilters),
        });
        setRowsFromPayload(response.data, fallbackRunning);
      } catch (err) {
        console.error("Error fetching Redis logs:", err);
        setError(getErrorMessage(err, "Failed to load Redis logs."));
      } finally {
        setLoading(false);
      }
    },
    [setRowsFromPayload]
  );

  const appendStreamEntries = useCallback((entries) => {
    if (!entries.length) return;

    const receivedAt = new Date().toISOString();
    const normalized = entries.map((entry) => {
      streamIndexRef.current += 1;
      return normalizeLogEntry(entry, `stream-${streamIndexRef.current}`, receivedAt);
    });

    setLogs((current) => {
      const knownIds = new Set(current.map((log) => String(log.id)));
      const additions = normalized.filter((log) => {
        const id = String(log.id);
        if (knownIds.has(id)) return false;
        knownIds.add(id);
        return true;
      });
      return [...current, ...additions].slice(-MAX_STREAM_ROWS);
    });
    setLastUpdatedAt(receivedAt);
  }, []);

  const openStream = useCallback(
    (requestedFilters = DEFAULT_FILTERS) => {
      closeStream();
      setError("");

      const streamParams = new URLSearchParams(
        sanitizeFilters(requestedFilters, false)
      ).toString();
      const streamPath = `${REDIS_LOGS_ENDPOINT}/stream${
        streamParams ? `?${streamParams}` : ""
      }`;
      const eventSource = new EventSource(buildApiUrl(streamPath), {
        withCredentials: true,
      });
      const receiveEntries = (event) => {
        setStreamConnected(true);
        setError("");
        appendStreamEntries(parseStreamPayload(event.data));
      };

      eventSourceRef.current = eventSource;
      setIsStreaming(true);
      setStreamConnected(false);

      eventSource.onopen = () => {
        if (eventSourceRef.current === eventSource) {
          setStreamConnected(true);
          setError("");
        }
      };
      eventSource.onmessage = receiveEntries;
      eventSource.addEventListener("redis-log", receiveEntries);
      eventSource.addEventListener("log", receiveEntries);
      eventSource.addEventListener("logs", receiveEntries);
      eventSource.onerror = () => {
        if (eventSourceRef.current !== eventSource) return;

        setStreamConnected(false);
        if (eventSource.readyState === EventSource.CLOSED) {
          setIsStreaming(false);
          setError("Redis log stream disconnected.");
        } else {
          setError("Redis log stream interrupted. Reconnecting...");
        }
      };
    },
    [appendStreamEntries, closeStream]
  );

  const handleStart = async () => {
    setActionLoading(true);
    setError("");

    try {
      const response = await api.post(`${REDIS_LOGS_ENDPOINT}/start`);
      const reconnectStream = isStreaming;
      if (reconnectStream) closeStream();
      setMonitorStatus((current) => ({
        ...current,
        running: true,
        status: "running",
      }));
      setStatusMessage(getPayloadMessage(response.data) || "Redis logging started.");
      await fetchLogs(appliedFilters, true);
      if (reconnectStream) openStream(appliedFilters);
    } catch (err) {
      console.error("Error starting Redis logs:", err);
      setError(getErrorMessage(err, "Failed to start Redis logging."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    setError("");

    try {
      const response = await api.post(`${REDIS_LOGS_ENDPOINT}/stop`);
      closeStream();
      setMonitorStatus((current) => ({
        ...current,
        running: false,
        status: "stopped",
      }));
      setStatusMessage(getPayloadMessage(response.data) || "Redis logging stopped.");
      await fetchLogs(appliedFilters, false);
    } catch (err) {
      console.error("Error stopping Redis logs:", err);
      setError(getErrorMessage(err, "Failed to stop Redis logging."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleStreamToggle = () => {
    if (isStreaming) {
      closeStream();
      return;
    }

    openStream(appliedFilters);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleApplyFilters = async (event) => {
    event.preventDefault();
    const sanitized = sanitizeFilters(filters);
    const nextFilters = {
      ...filters,
      limit: String(sanitized.limit || DEFAULT_FILTERS.limit),
    };
    const reconnectStream = isStreaming;

    if (reconnectStream) closeStream();
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    await fetchLogs(nextFilters);
    if (reconnectStream) openStream(nextFilters);
  };

  const handleClearFilters = async () => {
    const nextFilters = { ...DEFAULT_FILTERS };
    const reconnectStream = isStreaming;

    if (reconnectStream) closeStream();
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    await fetchLogs(nextFilters);
    if (reconnectStream) openStream(nextFilters);
  };

  const handleRefresh = async () => {
    const reconnectStream = isStreaming;

    if (reconnectStream) closeStream();
    await fetchLogs(appliedFilters);
    if (reconnectStream) openStream(appliedFilters);
  };

  const handleDeleteLog = async (logId) => {
    if (!logId || deletingLogId) return;
    if (!window.confirm("Are you sure you want to delete this log entry?")) return;

    setDeletingLogId(logId);
    setError("");

    try {
      await api.delete(`${REDIS_LOGS_ENDPOINT}/audit/${logId}`);
      setLogs((current) => current.filter((log) => String(log.id) !== String(logId)));
      setStatusMessage("Log entry deleted successfully.");
    } catch (err) {
      console.error("Error deleting Redis log:", err);
      setError(getErrorMessage(err, "Failed to delete the log entry."));
    } finally {
      setDeletingLogId(null);
    }
  };

  useEffect(() => {
    fetchLogs(DEFAULT_FILTERS);

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, [fetchLogs]);

  const filteredLogs = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return logs;

    return logs.filter((log) =>
      [
        log.timestamp,
        log.level,
        log.service,
        log.kind,
        log.event,
        log.traceId,
        log.bookingRef,
        log.orderId,
        log.paymentId,
        log.bookingStatus,
        log.outcome,
        log.lockType,
        log.lockStatus,
        log.summary,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [logs, searchText]);

  const activeFilterCount = useMemo(
    () => Object.keys(sanitizeFilters(appliedFilters, false)).length,
    [appliedFilters]
  );

  const streamLabel = isStreaming
    ? streamConnected
      ? "Live"
      : "Reconnecting"
    : "Offline";

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row g-3">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h3 className="fw-bold mb-1">Redis Booking Logs</h3>
              <div className="d-flex align-items-center gap-2 flex-wrap text-muted small">
                <span>{filteredLogs.length} records</span>
                {activeFilterCount > 0 && <span>{activeFilterCount} active filters</span>}
                {lastUpdatedAt && <span>Updated {formatDateTime(lastUpdatedAt)}</span>}
                {statusMessage && <span>{statusMessage}</span>}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span
                className={`badge ${monitorStatus.running ? "bg-success" : "bg-secondary"}`}
              >
                Monitor: {monitorStatus.status || (monitorStatus.running ? "running" : "stopped")}
              </span>
              {monitorStatus.connected !== null && (
                <span
                  className={`badge ${
                    monitorStatus.connected ? "bg-success" : "bg-danger"
                  }`}
                >
                  Redis: {monitorStatus.connected ? "Connected" : "Disconnected"}
                </span>
              )}
              {monitorStatus.bookingBufferSize !== null && (
                <span className="badge bg-light text-dark border">
                  Buffer: {monitorStatus.bookingBufferSize}
                </span>
              )}
              <span
                className={`badge ${
                  streamConnected
                    ? "bg-primary"
                    : isStreaming
                    ? "bg-warning text-dark"
                    : "bg-light text-dark border"
                }`}
              >
                Stream: {streamLabel}
              </span>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <RedisLogFilters
                filters={filters}
                loading={loading}
                actionLoading={actionLoading}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
              />

              <hr />

              <RedisLogTable
                filteredLogs={filteredLogs}
                loading={loading}
                actionLoading={actionLoading}
                searchText={searchText}
                onSearchTextChange={(event) => setSearchText(event.target.value)}
                onRefresh={handleRefresh}
                onStart={handleStart}
                onStop={handleStop}
                onStreamToggle={handleStreamToggle}
                isStreaming={isStreaming}
                monitorStatus={monitorStatus}
                error={error}
                onDeleteLog={handleDeleteLog}
                deletingLogId={deletingLogId}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedisLogs;
